import { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import { AgGrid } from "@jsq/ds/core";
import { client } from "~/apollo/client";
import { ComplianceCaseTypeEnum } from "~/graphql/supergraphEnums";
import { GetComplianceCasesSliceDocument } from "~/graphql/supergraphHooks";
import { type GetComplianceCasesSliceQuery } from "~/graphql/supergraphTypes";
import {
  ComplianceCaseFilters,
  ComplianceCaseSort,
  RowItem,
  TextFilter,
  ComplianceCaseRows,
} from "~/pages/Compliance/ComplianceTable/types";
import { DATE_FORMATS } from "~/utils/constants";
import { formatFullDateAndTimeWithLocalTimezone } from "~/utils/datetime";
import { toDbId } from "~/utils/graphql-helpers";
import { getCaseListDisplayName } from "../CaseDetailsDrawer/CaseDetailsHeader/utils";

const getFilterVars = (
  filterModel: AgGrid.FilterModel | AgGrid.AdvancedFilterModel | null,
): ComplianceCaseFilters[] | null => {
  if (!filterModel) {
    return null;
  }
  if (filterModel?.filterType) {
    throw Error("Handling for AgGrid.AdvancedFilterModel not implemented.");
  }
  const queryFilters: ComplianceCaseFilters[] = [];
  Object.entries(filterModel ?? {}).forEach((filter) => {
    const [field, filterInfo] = filter;
    // for now, we're not allowing multiple filters for field, so we won't hit this case
    if (filterInfo.conditions) {
      if (filterInfo.operator == "OR") {
        queryFilters.push({
          field: field,
          filterValues: filterInfo.conditions.map((condition: TextFilter) => condition.filter),
        });
      } else if (filterInfo.operator == "AND") {
        filterInfo.conditions.forEach((condition: TextFilter) => {
          queryFilters.push({ field: field, filterValues: [condition.filter] });
        });
      } else {
        // Shouldn't hit this error
        throw Error("Unexpected operator for multiple conditions");
      }
    } else if (filterInfo.filterType == "set") {
      queryFilters.push({
        field: field,
        filterValues: filterInfo.values.map((value: unknown) => String(value)),
      });
    } else if (filterInfo.filterType == "text") {
      queryFilters.push({ field: field, filterValues: [filterInfo.filter] });
    } else {
      // Would need to change this if we add other filter types
      throw Error("Unsupported filter type");
    }
  });
  return queryFilters;
};

const getSortVars = (sortItems: AgGrid.SortModelItem[]): ComplianceCaseSort[] | null => {
  if (!sortItems) {
    return null;
  }
  const querySorts: ComplianceCaseSort[] = [];
  sortItems.forEach((sort) => {
    querySorts.push({ field: sort.colId, sortAscending: sort.sort === "asc" });
  });
  return querySorts;
};

export const rowsFromQuery = (complianceCases: ComplianceCaseRows) => {
  const rowData: RowItem[] =
    complianceCases.map((complianceCase) => {
      const externalArena = complianceCase.externalArena;
      const entity = complianceCase.externalEntity;
      const position = complianceCase.position;
      // For EO cases linked via linkOnboardingCase, the backend sets externalObject to the
      // linked entity but leaves externalEntity null. Fall back to externalObject so the
      // entity name and ID are populated in both the table and the export filters.
      const resolvedEntity =
        entity ??
        (complianceCase.caseType === ComplianceCaseTypeEnum.EntityOnboarding
          ? complianceCase.externalObject
          : null);
      return {
        caseId: complianceCase.id,
        caseName: getCaseListDisplayName(complianceCase),
        programName: complianceCase.program?.name ?? "",
        casePrograms: complianceCase.casePrograms?.map((p) => p.name) ?? [],
        caseType: complianceCase.caseType,
        sourceCaseType: complianceCase.sourceCase?.caseType ?? null,
        client: externalArena?.name ?? "",
        clientId: externalArena?.objectId ?? "",
        entity: resolvedEntity?.name ?? "",
        entityId: resolvedEntity?.objectId ?? "",
        creationDate: formatFullDateAndTimeWithLocalTimezone(
          complianceCase.createdAt,
          DATE_FORMATS["MM/DD/YYYY"].long,
        ),
        status: complianceCase.status,
        subStatus: complianceCase.subStatus,
        resolution: complianceCase.resolution,
        assignee: complianceCase.assignee?.externalUser?.name ?? "",
        positionId: position?.id ? toDbId(position.id) : null,
        positionAccountName: position?.account?.name ?? null,
        positionEntityName: position?.investmentEntity?.name ?? resolvedEntity?.name ?? null,
      };
    }) ?? [];
  return rowData;
};

export class ComplianceGraphqlDataSource implements AgGrid.IServerSideDatasource {
  client: ApolloClient<NormalizedCacheObject>;
  private caseIds?: number[];

  constructor(caseIds?: number[]) {
    this.client = client;
    this.caseIds = caseIds;
  }

  public getRows(params: AgGrid.IServerSideGetRowsParams): void {
    const filters = getFilterVars(params.request.filterModel);
    const sorts = getSortVars(params.request.sortModel);
    const offset = params.request.startRow ?? 0;
    const limit = (params.request.endRow ?? 0) - offset;

    const allFilters: ComplianceCaseFilters[] = filters ?? [];
    if (this.caseIds && this.caseIds.length > 0) {
      allFilters.push({
        field: "id",
        filterValues: this.caseIds.map((id) => String(id)),
      });
    }

    this.client
      .query<GetComplianceCasesSliceQuery>({
        query: GetComplianceCasesSliceDocument,
        variables: {
          offset: offset,
          limit: limit,
          filters: allFilters.length > 0 ? allFilters : null,
          sorts: sorts,
        },

        fetchPolicy: "no-cache",
      })
      .then((response) => {
        const rowCount = response.data.complianceCasesSlice.filteredCount;
        if (rowCount === 0) {
          params.api.showNoRowsOverlay();
        } else {
          params.api.hideOverlay();
        }
        params.success({
          rowData: rowsFromQuery(response.data.complianceCasesSlice.rows),
          rowCount,
        });
      })
      .catch(() => {
        params.fail();
      });
  }
}
